import { Button } from "@/components/ui/button"
import { TypographyH1 } from "@/components/ui/typography"
import {
  authControllerGetSessions,
  authControllerRevokeOtherSessions,
  authControllerRevokeSession,
  type AuthSessionDto,
} from "@/shared/api/generated"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAutoTranslation } from "@/entities"
import { LANG_KEYS } from "@/shared/constants"

const AUTH_SESSIONS_KEY = ["AUTH_SESSIONS"]

const textValue = (value: unknown) =>
  typeof value === "string" && value ? value : null

const sessionTitle = (session: AuthSessionDto) => {
  const deviceName = textValue(session.deviceName)
  const browser = textValue(session.browser)
  const osName = textValue(session.osName)
  if (deviceName) return deviceName
  if (browser && osName) {
    return `${browser} on ${osName}`
  }
  return osName || session.clientType.toLowerCase()
}

export const AuthSessionsPage = () => {
  const queryClient = useQueryClient()
  const { t } = useAutoTranslation()
  const sessions = useQuery({
    queryKey: AUTH_SESSIONS_KEY,
    queryFn: () => authControllerGetSessions(),
  })
  const revoke = useMutation({
    mutationFn: (sessionId: string) => authControllerRevokeSession(sessionId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: AUTH_SESSIONS_KEY }),
  })
  const revokeOthers = useMutation({
    mutationFn: () => authControllerRevokeOtherSessions(),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: AUTH_SESSIONS_KEY }),
  })

  const otherSessions = sessions.data?.filter(session => !session.current) ?? []

  return (
    <div className="h-full p-4">
      <TypographyH1 className="mb-2 text-left text-2xl">
        {t(LANG_KEYS.ACTIVE_SESSIONS)}
      </TypographyH1>
      <p className="text-muted-foreground mb-5 text-sm">
        {t(LANG_KEYS.ACTIVE_SESSIONS_DESCRIPTION)}
      </p>

      {sessions.isLoading && <p>{t(LANG_KEYS.LOADING_ACTIVE_SESSIONS)}</p>}
      {sessions.isError && (
        <p className="text-destructive text-sm">
          {t(LANG_KEYS.COULD_NOT_LOAD_ACTIVE_SESSIONS)}
        </p>
      )}

      <div className="space-y-3">
        {sessions.data?.map(session => (
          <div
            key={session.id}
            className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <div className="font-semibold">
                {sessionTitle(session)}
                {session.current && (
                  <span className="text-primary ml-2 text-sm font-normal">
                    {t(LANG_KEYS.THIS_BROWSER)}
                  </span>
                )}
              </div>
              <div className="text-muted-foreground mt-1 text-sm">
                {t(LANG_KEYS.LAST_ACTIVE)} {new Date(session.lastUsedAt).toLocaleString()}
              </div>
              {textValue(session.country) && (
                <div className="text-muted-foreground text-sm">
                  {t(LANG_KEYS.COUNTRY)}: {textValue(session.country)}
                </div>
              )}
            </div>

            {!session.current && (
              <Button
                variant="destructive"
                isLoading={revoke.isPending && revoke.variables === session.id}
                onClick={() => {
                  if (
                    window.confirm(
                      t(LANG_KEYS.END_THIS_SESSION)
                    )
                  ) {
                    revoke.mutate(session.id)
                  }
                }}
              >
                {t(LANG_KEYS.REVOKE)}
              </Button>
            )}
          </div>
        ))}
      </div>

      {otherSessions.length > 0 && (
        <Button
          className="mt-5"
          variant="outline"
          isLoading={revokeOthers.isPending}
          onClick={() => {
            if (
              window.confirm(
                t(LANG_KEYS.OTHER_SESSIONS_SIGN_IN_AGAIN)
              )
            ) {
              revokeOthers.mutate()
            }
          }}
        >
          {t(LANG_KEYS.REVOKE_ALL_OTHER_SESSIONS)}
        </Button>
      )}
    </div>
  )
}
